import {Injectable} from '@angular/core';
import {StateService} from '@core/services/state.service';
import {SexFilter, StateFilter, StateLeagueFilter} from '@shared/components/filter/filter';
import {NewsHttpService, NewsTargetType} from '@api/http/news/news-http.service';
import {INewsListResponse} from '@api/models/news/news-request';
import {INewsPost} from '@api/models/news/news';
import {FilterService} from '@shared/components/filter/filter.service';

@Injectable({
  providedIn: 'root',
})
export class HomeNewsTabService {

  public newsEntries: INewsPost[];
  public topNewsEntries: INewsPost[];
  public loading = false;
  public moreEntriesAvailable = false;
  private pageSize = 10;

  public readonly sexFilter: SexFilter;
  public readonly leagueFilter: StateLeagueFilter;
  public readonly stateFilter: StateFilter;

  constructor( private newsService: NewsHttpService,
               private filterService: FilterService) {
    this.sexFilter = this.filterService.create(SexFilter);
    this.stateFilter = this.filterService.create(StateFilter);
    this.leagueFilter = this.filterService.create(StateLeagueFilter, this.sexFilter, this.stateFilter);
    this.sexFilter.selectOption();
  }

  public async onSexFilterSelectionChange($event) {
    this.leagueFilter.reset();
    await this.loadPaginatedNews(false, true);
  }

  public async onStateFilterSelectionChange($event) {
    this.leagueFilter.reset();
    await this.loadPaginatedNews(false, true);
  }

  public async onLeagueFilterSelectionChange($event) {
    await this.loadPaginatedNews(false, true);
  }

public async loadPaginatedNews(initialLoad = false, resetEntries = false) {
    try {
      if (!this.newsEntries || resetEntries) {
        this.newsEntries = [];
      }

      const [offset, limit] = this.calculatePaginationSettings(initialLoad);

      this.loading = true;
      let response: INewsListResponse;

      const [filterType, filterValue] = this.getActiveFilterTypeAndValue();

      if (filterType) {
        response = await this.newsService.getNewsForTargetPaginated(filterType,
          filterValue,
          offset,
          limit,
          false,
          false,
          false,
          this.sexFilter.selectedOptionValue)
      } else {
        response = await this.newsService.getLatestNewsPaginated(offset, limit, false, false, this.sexFilter.selectedOptionValue);
      }

      this.updateEntries(initialLoad, response);
      this.loading = false;

    } catch (e) {
      this.loading = false;
      throw e;
    }
  }

  public async loadTopNews() {
    const response = await this.newsService.getTopNews(28);
    if (response) {
      // don't show news from homepage, TODO: rework once paginated topNews available
      this.topNewsEntries = response.slice(-20);
    }
  }

  private calculatePaginationSettings(initialLoad: boolean): [number, number] {
    const offset = initialLoad ? 0 : this.newsEntries.length;

    let pageSize;
    if (initialLoad && !StateService.isBrowser) {
      pageSize = 2; // nr of news above the fold
    } else {
      pageSize = this.pageSize;
    }

    return [offset, pageSize];
  }

  private updateEntries(initialLoad: boolean, response: INewsListResponse): void {
    if (response?.data) {
      if (initialLoad) {
        // Only update if the list changes
        if (JSON.stringify(this.newsEntries) !== JSON.stringify(response.data)) {
          this.newsEntries = response.data;
        }
      } else {
        this.newsEntries.push(...response.data);
      }

      this.moreEntriesAvailable = this.newsEntries.length < response.total_count;
    }
  }

  private getActiveFilterTypeAndValue(): [NewsTargetType, string] {
    if (this.leagueFilter.active) {
      return [NewsTargetType.League, this.leagueFilter.selectedOptionValue];
    } else if (this.stateFilter.active) {
      return [NewsTargetType.StateOnly, this.stateFilter.selectedOptionValue];
    } else {
      return [null, null];
    }
  }
}
